import {
  AppointmentStatus,
  ScheduleMethod,
  SchedulePortalType,
  SchedulePortalIntegrationMethod,
  PortalDatePolicy,
} from '../enums';
import { Address } from './address';
import { StopType } from './stop';
import { makeStandardContactSchema } from './contact';
import { z } from 'zod';

export type ScheduleStop = {
  movementStopId: string;
  locationLabel: string;
  placeId: string;
  suggestedDt?: Date;
  appointmentStatus?: AppointmentStatus;
  appointmentRequestedDt?: Date;
  appointmentConfirmedDt?: Date;
  appointmentConfirmationNumber?: string;
  stopEmails: string[];
  stopPhoneNumbers?: string[];
  placeSchedulePortalType?: SchedulePortalType;
  placeSchedulePortalUIType?: SchedulePortalIntegrationMethod;
  portalAvailTimes?: Date[];
  portalInfo?: string;
  placeTimeZone: string;
  day?: string;
  stopType: StopType;
  address: Address;
  scheduleMethod?: ScheduleMethod;
  schedulingMethods?: StandardSchedulingMethod[];
  scheduleNotes?: string;
};

export type OpendockAvailabilityInputFields = {
  day?: string;
  warehouseId: string;
  loadTypeId?: string;
  dockId?: string;
};

// TODO(jonathan): Probably merge these in with PortalDetails
export type OpendockScheduleAppointmentInputFields = {
  day?: string;
  warehouseId: string;
  loadTypeId: string;
  dockId: string;
};

export type PortalTypeSpecificGetAvailabilityInputFields =
  OpendockAvailabilityInputFields;

export type PortalTypeSpecificScheduleApptInputFields =
  OpendockScheduleAppointmentInputFields;

export type PortalDetails = {
  day?: string;
  datePolicy?: PortalDatePolicy;
  scheduleNotes?: string;
};

export const makeStandardOpendockScheduleAppointmentInputFieldsSchema = () => {
  return z.object({
    day: z.string().optional(),
    warehouseId: z.string().optional(),
    loadTypeId: z.string().optional(),
    dockId: z.string().optional(),
  });
};

export const makeSchedulePortalTypeSchema = () =>
  z.nativeEnum(SchedulePortalType);

const genericSchedulePortalTypes = [
  SchedulePortalType.BLUE_YONDER,
  SchedulePortalType.COSTCO,
  SchedulePortalType.DSDLINK,
  SchedulePortalType.E2OPEN,
  SchedulePortalType.LMCONNECT,
  SchedulePortalType.MANHATTAN,
  SchedulePortalType.RYDERSHARE,
  SchedulePortalType.YARDVIEW,
] as const;

const makeStandardGenericPortalSchedulingSchema = () =>
  z.object({
    type: z.enum(genericSchedulePortalTypes),
  });

export const makeStandardPortalSchedulingSchema = () => {
  return z.discriminatedUnion('type', [
    z.object({
      type: z.literal(SchedulePortalType.NCR),
      companyName: z.string().optional(),
    }),
    z.object({
      type: z.literal(SchedulePortalType.OPENDOCK),
      inputFields:
        makeStandardOpendockScheduleAppointmentInputFieldsSchema().optional(),
    }),
    makeStandardGenericPortalSchedulingSchema(),
  ]);
};
const standardPortalSchedulingSchema = makeStandardPortalSchedulingSchema();
export type StandardPortalScheduling = z.infer<
  typeof standardPortalSchedulingSchema
>;

export const makeStandardEmailSchedulingMethodSchema = () => {
  return z.object({
    type: z.literal(ScheduleMethod.EMAIL),
    contact: makeStandardContactSchema(),
  });
};
const standardEmailSchedulingMethodSchema =
  makeStandardEmailSchedulingMethodSchema();
export type StandardEmailSchedulingMethod = z.infer<
  typeof standardEmailSchedulingMethodSchema
>;

export const makeStandardPhoneSchedulingMethodSchema = () => {
  return z.object({
    type: z.literal(ScheduleMethod.PHONE),
    contact: makeStandardContactSchema(),
  });
};
const standardPhoneSchedulingMethodSchema =
  makeStandardPhoneSchedulingMethodSchema();
export type StandardPhoneSchedulingMethod = z.infer<
  typeof standardPhoneSchedulingMethodSchema
>;

export const makeStandardPortalSchedulingMethodSchema = () => {
  return z.object({
    type: z.literal(ScheduleMethod.PORTAL),
    details: makeStandardPortalSchedulingSchema(),
  });
};
const standardPortalSchedulingMethodSchema =
  makeStandardPortalSchedulingMethodSchema();
export type StandardPortalSchedulingMethod = z.infer<
  typeof standardPortalSchedulingMethodSchema
>;

export const makeStandardSchedulingMethodSchema = () => {
  return z.discriminatedUnion('type', [
    makeStandardEmailSchedulingMethodSchema(),
    makeStandardPhoneSchedulingMethodSchema(),
    makeStandardPortalSchedulingMethodSchema(),
  ]);
};
const standardSchedulingMethodSchema = makeStandardSchedulingMethodSchema();
export type StandardSchedulingMethod = z.infer<
  typeof standardSchedulingMethodSchema
>;

export const makeStandardSchedulingSchema = () => {
  return z.object({
    methods: z.array(makeStandardSchedulingMethodSchema()).optional(),
    notes: z.string().optional(),
    maxDaysAhead: z.number().optional(),
  });
};
const standardSchedulingSchema = makeStandardSchedulingSchema();
export type StandardScheduling = z.infer<typeof standardSchedulingSchema>;
